import {put, call} from 'redux-saga/effects';
import Participant from "../../entities/participant";
import * as Actions from "../actions/participantActions";
import * as idb from '../../lib/idbd';
import * as api from '../../rest/api';
import {processQueue} from "../../queues/processQueue";
import newHRTimestamp from "../../lib/picos/timestamp";
import {tabSession} from '../../Auth/session';

// Inlined to reduce circular dependencies
function getCurrentUserId() {
    return sessionStorage.getItem('userId');
}

// Inlined to reduce circular dependencies
function getCurrentFirmId() {
    return sessionStorage.getItem('firmId');
}

type ParticipantAction = {
    participantId: string;
    participant: Participant;
    participants: Participant[];
    onQueued?: (saved: boolean) => void;
};

export function* fetchOneParticipant(action: ParticipantAction) {
	const session = tabSession.captureGeneration();
	try {
		const participant: Participant = yield call(api.findOne, 'participant', action.participantId);
		session.assertCurrent();

		// Now we can update the user interface
		yield put({type: Actions.PARTICIPANT_FETCH_ONE_SUCCESS, participantId: action.participantId, participant});
	}
	catch(err) {
		if (session.signal.aborted) return;
		yield put({type: Actions.PARTICIPANT_FETCH_ONE_FAILURE, participantId: action.participantId, status: 500, err});

		console.error('Error fetching from database', err);
	}
}

/**
 * @param {ParticipantAction} action
 */
export function* saveParticipant(action: ParticipantAction) {
    const session = tabSession.captureGeneration();
    let queueReported = false;
    const reportQueued = (saved: boolean) => {
        if (!queueReported) { queueReported = true; action.onQueued?.(saved); }
    };
    // console.debug('⮂ PARTICIPANT_SAVE saga', action.participant?.id);
    
	if (action.participant === undefined || action.participant === null) {
		console.error('saveParticipant received a null');
		throw new Error('saveParticipant received a null');
	}

	if (!(action.participant instanceof Participant)) {
		action.participant = new Participant(action.participant);
	}

	try {
		// Save to IndexedDB
		const addResult = yield call(idb.add, 'participant', action.participant);
		if (addResult.isErr()) {
			console.error(`⮂ saveParticipant: IDB write failed: ${addResult.unwrapErr().message}`);
			reportQueued(false);
			session.assertCurrent();
			yield put({type: Actions.PARTICIPANT_SAVE_FAILURE, participant: action.participant, status: 500, message: addResult.unwrapErr().message});
			return;
		}
		reportQueued(true);
		session.assertCurrent();

		// Now we can update the user interface
		yield put({type: Actions.PARTICIPANT_SAVE, participant: action.participant});

		// A save never deletes autosaved drafts (OWA-958). The editor that wrote a draft
		// removes it when it saves; a draft typed somewhere else may be newer than this copy.
		session.assertCurrent();

		// Let the queue know to process
		yield call(processQueue);
	}
	catch(err) {
		if (session.signal.aborted) { reportQueued(false); return; }
		reportQueued(false);
		yield put({type: Actions.PARTICIPANT_SAVE_FAILURE, participant: action.participant, status: 500, err});

		console.error('Error saving to database', err);
	}
}

/**
 * TODO: Update for saveMany
 * @param {ParticipantAction} action
 * @param {Participant[]} action.participants
 */
export function* saveManyParticipants(action: ParticipantAction) {
    const session = tabSession.captureGeneration();
    let queueReported = false;
    const reportQueued = (saved: boolean) => {
        if (!queueReported) { queueReported = true; action.onQueued?.(saved); }
    };
    // console.debug('⮂ PARTICIPANT_SAVE_MANY saga', action.participants.map(participant => participant.id).join(','));
    
    if (!Array.isArray(action.participants)) {
        console.error('saveManyParticipants expected an array, received', action.participants);
        throw new Error('saveManyParticipants expected an array');
    }

	try {
		const toSave: Participant[] = [];

		// Save each item to IndexedDB
		for(let i = 0; i < action.participants.length; i++) {
			const participant = action.participants[i];
			participant.persisted = false;
			toSave.push(participant);
		}

		const addResult = yield call(idb.add, 'participant', toSave);
		if (addResult.isErr()) {
			console.error(`⮂ saveManyParticipants: IDB write failed: ${addResult.unwrapErr().message}`);
			reportQueued(false);
			session.assertCurrent();
			yield put({type: Actions.PARTICIPANT_SAVE_MANY_FAILURE, participants: action.participants, status: 500, message: addResult.unwrapErr().message});
			return;
		}
		reportQueued(true);
		session.assertCurrent();

		// Now we can update the user interface
		yield put({type: Actions.PARTICIPANT_SAVE_MANY, participants: toSave});
		session.assertCurrent();

		// Let the queue know to process
		yield call(processQueue)
	}
	catch(err) {
		if (session.signal.aborted) { reportQueued(false); return; }
		reportQueued(false);
		yield put({type: Actions.PARTICIPANT_SAVE_MANY_FAILURE, participants: action.participants, status: 500, err});

		console.error(`Error saving ${action.participants.length} participants to database`, err);
	}
}

/**
 * @param {ParticipantAction} action
 */
export function* removeParticipant(action: ParticipantAction) {
    const session = tabSession.captureGeneration();
    const userId = getCurrentUserId();
	if (!userId || action.participant === undefined || action.participant === null || !(action.participant instanceof Participant)) {
			console.error('removeParticipant expected type Participant, received', action.participant);
			throw new Error('removeParticipant expected type Participant');
		}

	try {
		// Mark as deleted
		action.participant.status = 'D';
		action.participant.addedOn = newHRTimestamp();
		action.participant.addedBy = userId;

		// Save to IndexedDB
		const addResult = yield call(idb.add, 'participant', action.participant);
		session.assertCurrent();
		if (addResult.isErr()) {
			console.error(`⮂ removeParticipant: IDB write failed: ${addResult.unwrapErr().message}`);
			yield put({type: Actions.PARTICIPANT_REMOVE_FAILURE, participant: action.participant, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.PARTICIPANT_REMOVE, participant: action.participant});
		session.assertCurrent();

		// Let the queue know to process
		yield call(processQueue)
	}
	catch(err) {
		if (session.signal.aborted) return;
		yield put({type: Actions.PARTICIPANT_REMOVE_FAILURE, participant: action.participant, status: 500, err});

		console.error('Error deleting from database', err);
	}
}

/**
	* Restore the last version of this Participant.
	*
	* @param {ParticipantAction} action
	*/
export function* restoreParticipant(action: ParticipantAction) {
    const session = tabSession.captureGeneration();
    const userId = getCurrentUserId();
    if (!userId) {
        console.error('restoreParticipant expected getCurrentUserId() to return a value');
        return;
    }
    
	if (action.participant === undefined || action.participant === null) {
        console.error('restoreParticipant expected a Participant, received null');
        throw new Error('restoreParticipant expected a Participant, received null');
    }
    else if (!(action.participant instanceof Participant)) action.participant = new Participant(action.participant); 
    
	try {
		// Mark as restored
		action.participant.status = 'U';
		action.participant.addedOn = newHRTimestamp();
		action.participant.addedBy = userId;
		action.participant.restore = true;

		// Save to IndexedDB
		const addResult = yield call(idb.add, 'participant', action.participant);
		session.assertCurrent();
		if (addResult.isErr()) {
			console.error(`⮂ restoreParticipant: IDB write failed: ${addResult.unwrapErr().message}`);
			yield put({type: Actions.PARTICIPANT_RESTORE_FAILURE, participant: action.participant, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.PARTICIPANT_RESTORE, participant: action.participant});
	}
	catch(err) {
		if (session.signal.aborted) return;
		yield put({type: Actions.PARTICIPANT_RESTORE_FAILURE, participant: action.participant, status: 500, err});

		console.error('Error restoring from database', err);
	}
}